import { Download } from 'lucide-react';
import * as React from 'react';

import { Button, toast } from '@/components/cargofl-ui';
import { apiDownload } from '@/lib/api';
import { useCurrentUser } from '@/lib/session';

/**
 * Export the WHOLE filtered result set, from the server.
 *
 * The difference from `ExportCsvButton` is the only thing that matters here: that one
 * serialises the rows React is currently holding, so exporting a paginated table gives
 * you page one and says nothing about the other four hundred rows. This sends the same
 * query the screen is showing to the report route, which pages through the lot.
 *
 * Use this wherever the table is paginated or filtered server-side. Where the screen
 * already holds every row — an aggregate table, a manager's own direct reports —
 * `ExportCsvButton` is still the right one, and cheaper.
 *
 * Hidden, not disabled, for anyone outside the report routes' roles. A button that
 * always 403s is worse than no button: it reads as something broken rather than
 * something that was never theirs.
 */
export function ServerExportButton({
  path,
  filename,
  label = 'Export all',
  extension = 'csv',
}: {
  /** Report route including its query string, e.g. `/reports/goals.csv?status=DRAFT`. */
  path: string;
  /** Used only if the server's Content-Disposition is unreadable. */
  filename: string;
  label?: string;
  /** Extension for the fallback filename. */
  extension?: 'csv' | 'xlsx';
}) {
  const me = useCurrentUser();
  const [busy, setBusy] = React.useState(false);

  // Exactly the @Roles on ReportsController. DEPT_HEAD reads it as oversight.
  if (!['HR', 'SUPER_ADMIN', 'DEPT_HEAD'].includes(me.role)) return null;

  async function onExport() {
    setBusy(true);
    try {
      await apiDownload(path, `${filename}-${new Date().toISOString().slice(0, 10)}.${extension}`);
    } catch (e) {
      toast.error('Could not export', {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button hint={"Download the full report as a spreadsheet"}
      size="sm"
      variant="outline"
      className="gap-1.5"
      disabled={busy}
      onClick={() => void onExport()}
    >
      <Download className="size-3.5" />
      {busy ? 'Exporting…' : label}
    </Button>
  );
}
